import { useState, type FormEvent } from "react";

import { Button, Modal, Toggle } from "@/components/common";
import { extractErrorMessage } from "@/services/axiosConfig";
import { useCreateContact, useUpdateContact } from "@/hooks/useClients";
import type { ClientContact } from "@/types/client";

type Props = {
  workspaceId: number;
  clientId: number;
  contact?: ClientContact;
  onClose: () => void;
};

export function ContactFormModal({ workspaceId, clientId, contact, onClose }: Props) {
  const createMut = useCreateContact(workspaceId, clientId);
  const updateMut = useUpdateContact(workspaceId, clientId);
  const editing = contact != null;

  const [name, setName] = useState(contact?.name ?? "");
  const [role, setRole] = useState(contact?.role ?? "");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [marketing, setMarketing] = useState(contact?.accepts_marketing ?? false);
  const [error, setError] = useState<string | null>(null);

  const busy = createMut.isPending || updateMut.isPending;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const payload = {
        name: name.trim(),
        role: role.trim() || undefined,
        email: email.trim() || undefined,
        phone: phone.trim() || undefined,
        accepts_marketing: marketing,
      };
      if (editing) {
        await updateMut.mutateAsync({ contactId: contact.id, payload });
      } else {
        await createMut.mutateAsync(payload);
      }
      onClose();
    } catch (err) {
      setError(extractErrorMessage(err));
    }
  }

  return (
    <Modal
      onClose={onClose}
      title={editing ? "Editar contato" : "Novo contato"}
      footer={
        <>
          <Button variant="ghost" type="button" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" type="submit" form="contact-form" disabled={busy}>
            {busy ? "Salvando…" : "Salvar"}
          </Button>
        </>
      }
    >
      <form
        id="contact-form"
        onSubmit={onSubmit}
        style={{ display: "flex", flexDirection: "column", gap: 14 }}
      >
        {error && <div className="form-error">{error}</div>}
        <label className="form-field">
          <span>Nome</span>
          <input className="form-input" value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
        </label>
        <label className="form-field">
          <span>Função</span>
          <input
            className="form-input"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            placeholder="Decisor, financeiro, contador…"
          />
        </label>
        <label className="form-field">
          <span>E-mail</span>
          <input
            className="form-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={contact?.email_masked ?? ""}
          />
        </label>
        <label className="form-field">
          <span>Telefone</span>
          <input
            className="form-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder={contact?.phone_masked ?? ""}
            inputMode="tel"
          />
        </label>
        <Toggle
          checked={marketing}
          onChange={setMarketing}
          label="Aceita marketing"
          sub="Pode receber campanhas de e-mail"
        />
      </form>
    </Modal>
  );
}
